import type { MantineNumberSize } from '@mantine/core';
import { Box, Flex, useMantineTheme } from '@mantine/core';
import * as Plot from '@observablehq/plot';
import { Icon } from '@repo/foundations';
import { Text } from '@repo/foundations/components/Text/Text';
import { isNil } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChartTooltip } from '../../components/Chart/components/ChartTooltip';
import { pointerX } from './TooltipMark/pointer';
import { cleanUpTooltips, tooltip } from './TooltipMark/tooltipMark';
import { getPointValueFromChannel } from './utils';

type PlotElement = (HTMLElement | SVGSVGElement) & { remove: () => void };

function wrapToTwoLines(text: string, maxCharsPerLine: number): string {
	const words = text.split(' ');
	let line1 = '';
	let line2 = '';
	for (let i = 0; i < words.length; i += 1) {
		const next = line1.length === 0 ? words[i] : `${line1} ${words[i]}`;
		if (next.length <= maxCharsPerLine) {
			line1 = next;
		} else {
			line2 = words.slice(i).join(' ');
			break;
		}
	}
	if (!line2) return line1;
	return `${line1}\n${line2}`;
}

interface StackedBarChartChannel<T, X = Date, Y = number> {
	label: string;
	color: string | keyof T | ((d: T) => string);
	x: keyof T | ((d: T) => X);
	y: keyof T | ((d: T) => Y);
}

interface StackedBarChartAxis<P> {
	formatter?: (point: P) => string;
	tooltipFormatter?: (point: P) => string;
	maxTicks?: number;
	visible?: boolean;
	// When true, attempts to wrap long tick labels onto two lines
	wordWrapTwoLines?: boolean;
	// Optional maximum characters per line for word wrap (default: 16)
	maxCharsPerLine?: number;
}

export interface StackedBarChartProps<T, X = Date, Y = number> {
	channels: Array<StackedBarChartChannel<T, X, Y>>;
	data: Array<T>;
	labelX: string;
	width?: MantineNumberSize;
	height?: MantineNumberSize;
	xAxis?: StackedBarChartAxis<X>;
	yAxis?: StackedBarChartAxis<Y>;
	tooltipRenderer?: (dataIndex: number) => React.ReactNode;
	/** https://observablehq.com/plot/features/intervals#utcInterval */
	interval?: Plot.RangeInterval;
	/** Additional Plot.js options to override defaults */
	chartProps?: Partial<Plot.PlotOptions>;
	/** Optional empty state handling similar to StackedLineChart */
	showEmptyStateMessage?: boolean;
	emptyStateMessage?: { title: string; description: string };
}

export function StackedBarChart<T, X = Date, Y = number>({
	channels,
	data,
	labelX,
	width,
	height,
	xAxis,
	yAxis,
	tooltipRenderer,
	interval,
	chartProps,
	showEmptyStateMessage,
	emptyStateMessage,
}: StackedBarChartProps<T, X, Y>) {
	const theme = useMantineTheme();
	const containerRef = useRef<HTMLDivElement>(null);
	const [chartWidth, setChartWidth] = useState<number | undefined>();
	const [error, setError] = useState<Error | null>(null);

	const getChannelColorValue = useCallback(
		(channel: StackedBarChartChannel<T, X, Y>, item: T): string => {
			if (typeof channel.color === 'function') {
				return channel.color(item);
			}
			if (typeof channel.color === 'string') {
				return channel.color;
			}
			// channel.color is keyof T, so we extract the value and convert to string
			const colorValue = getPointValueFromChannel(channel.color, item);
			return String(colorValue || '');
		},
		[]
	);

	const transformedData = useMemo(
		() =>
			data.flatMap((item, index) =>
				channels.map((channel) => ({
					x: getPointValueFromChannel(channel.x, item),
					y: getPointValueFromChannel(channel.y, item),
					color: getChannelColorValue(channel, item),
					series: channel.label,
					dataItemIndex: index,
				}))
			),
		[channels, data, getChannelColorValue]
	);

	const renderTooltip = useCallback(
		({
			x,
			y,
			dataIndex,
		}: {
			// eslint-disable-next-line react/no-unused-prop-types
			x: number;
			// eslint-disable-next-line react/no-unused-prop-types
			y: number;
			// eslint-disable-next-line react/no-unused-prop-types
			dataIndex: number | null;
		}) => {
			if (
				!tooltipRenderer ||
				dataIndex === null ||
				dataIndex === undefined ||
				!transformedData[dataIndex]
			) {
				return null;
			}

			const originalIndex = transformedData[dataIndex].dataItemIndex;
			const content = tooltipRenderer(originalIndex);

			return (
				<ChartTooltip x={x + 8} y={y} w="auto" miw={260}>
					<Box>{content}</Box>
				</ChartTooltip>
			);
		},
		[transformedData, tooltipRenderer]
	);

	/**
	 * Updates the chart width when the container size changes.
	 * Used by the resize observer to maintain responsive behavior.
	 */
	const updateChartWidth = useCallback(() => {
		if (containerRef.current) {
			setChartWidth(containerRef.current.clientWidth);
		}
	}, []);

	// Set up resize observer to maintain responsive chart dimensions
	useEffect(() => {
		updateChartWidth();

		const resizeObserver = new ResizeObserver(() => {
			updateChartWidth();
		});

		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, [updateChartWidth]);

	useEffect(() => {
		if (!containerRef.current || transformedData.length === 0) return undefined;

		let plot: PlotElement | null = null;
		try {
			plot = Plot.plot({
				width: chartWidth,
				height: typeof height === 'number' ? height : undefined,
				marginTop: 10,
				marginBottom:
					isNil(xAxis?.visible) || xAxis.visible
						? xAxis?.wordWrapTwoLines
							? 56
							: 40
						: 8,
				marginLeft: 56,
				marginRight: 16,
				x: {
					ticks: isNil(xAxis?.visible) || xAxis.visible ? xAxis?.maxTicks : 0,
					label: null,
					nice: true,
					interval,
				},
				y: {
					ticks: isNil(yAxis?.visible) || yAxis.visible ? yAxis?.maxTicks : 0,
					label: null,
				},
				marks: [
					...(isNil(xAxis?.visible) || xAxis.visible
						? [
								Plot.axisX({
									anchor: 'bottom',
									label: null,
									ticks: xAxis?.maxTicks,
									tickSize: 0,
									fontSize: 12,
									color: theme.other.getColor('text/secondary/default'),
									tickPadding: 12,
									textAnchor: 'middle',
									tickFormat: xAxis?.formatter,
								}),
							]
						: []),
					...(isNil(yAxis?.visible) || yAxis.visible
						? [
								Plot.axisY({
									label: null,
									ticks: yAxis?.maxTicks,
									tickSize: 0,
									fontSize: 12,
									color: theme.other.getColor('text/secondary/default'),
									tickPadding: 16,
									tickFormat: yAxis?.formatter,
								}),
							]
						: []),
					// Stacked bars with per-bar colors
					Plot.barY(
						transformedData,
						Plot.stackY({
							x: 'x',
							y: 'y',
							fill: 'color',
						})
					),
					// Vertical hover rule for time-based charts; harmless for categorical axes
					Plot.ruleX(
						transformedData,
						pointerX({
							x: 'x',
							stroke: theme.other.getColor('border/secondary/default'),
						})
					),
					// Tooltips - only add if tooltipRenderer is provided
					...(tooltipRenderer
						? [
								tooltip(
									transformedData,
									pointerX({
										x: 'x',
										tooltipRenderer: renderTooltip,
									})
								),
							]
						: []),
				],
				...chartProps,
			});

			// Clear previous and append
			containerRef.current.innerHTML = '';
			if (plot) containerRef.current.appendChild(plot);

			// Optional two-line word wrap for categorical labels
			if (xAxis?.wordWrapTwoLines) {
				const maxChars = xAxis.maxCharsPerLine ?? 16;
				const textNodes = containerRef.current.querySelectorAll(
					'g[aria-label*="x-axis"] text'
				);
				textNodes.forEach((textEl) => {
					const full = textEl.textContent || '';
					if (!full) return;
					const wrapped = wrapToTwoLines(full, maxChars);
					if (wrapped.includes('\n')) {
						textEl.textContent = '';
						const [line1, line2] = wrapped.split('\n');
						const x = textEl.getAttribute('x') || '0';
						const t1 = document.createElementNS(
							'http://www.w3.org/2000/svg',
							'tspan'
						);
						t1.setAttribute('x', x);
						t1.setAttribute('dy', '0');
						t1.textContent = line1;
						textEl.appendChild(t1);
						const t2 = document.createElementNS(
							'http://www.w3.org/2000/svg',
							'tspan'
						);
						t2.setAttribute('x', x);
						t2.setAttribute('dy', '1.1em');
						t2.textContent = line2;
						textEl.appendChild(t2);
					}
				});
			}
		} catch (err) {
			setError(err instanceof Error ? err : new Error(String(err)));
		}

		return () => {
			try {
				if (plot) plot.remove();
				cleanUpTooltips();
			} catch {
				// no-op
			}
		};
	}, [
		chartWidth,
		height,
		theme,
		labelX,
		xAxis?.visible,
		xAxis?.maxTicks,
		xAxis?.formatter,
		xAxis?.wordWrapTwoLines,
		xAxis?.maxCharsPerLine,
		yAxis?.visible,
		yAxis?.maxTicks,
		yAxis?.formatter,
		tooltipRenderer,
		renderTooltip,
		transformedData,
		interval,
		chartProps,
	]);

	if (error) {
		return (
			<Flex
				h={typeof height === 'number' ? height : 200}
				w="100%"
				justify="center"
				align="center"
				style={{
					backgroundColor: theme.other.getColor('fill/tertiary/default'),
					borderRadius: theme.radius.md,
				}}
				role="alert"
			>
				<Text color="text/critical/default">Error rendering chart</Text>
			</Flex>
		);
	}

	if (showEmptyStateMessage && data.length === 0) {
		return (
			<Flex
				h={typeof height === 'number' ? height : 200}
				w="100%"
				justify="center"
				direction="column"
				align="center"
				style={{
					backgroundColor: theme.other.getColor('fill/primary/default'),
					borderRadius: theme.radius.md,
				}}
				role="region"
				aria-label="Empty chart state"
			>
				<Icon name="chartBar" size="lg" iconPadding="xs" />
				<Text size="sm" fw={500} color="text/primary/default">
					{emptyStateMessage?.title || 'No data available'}
				</Text>
				<Text size="sm" color="text/secondary/default">
					{emptyStateMessage?.description ||
						'Select a different time range or data source'}
				</Text>
			</Flex>
		);
	}

	return <Box w={width} h={height} ref={containerRef} />;
}
